import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { EnumSituacao } from '../../enums/situacao.enum';
import { IMultiSelectSettings } from '../multi-select/models/multi-select-settings.interface';
import { MultiSelectComponent } from '../multi-select/multi-select.component';
import { IOption } from '../multi-select/models/option.interface';
import { ApiError } from 'app/core';
import { CustomControlValueAccessor } from '../../utils/custom-control-value-accessor/custom-control-value-accessor';
import { IPeriodicidadeCompraFilter } from 'app/domains/environment/periodicidade-compra/interfaces/periodicidade-compra-filter.interface';
import { PeriodicidadeCompraApi } from 'app/domains/environment/periodicidade-compra/api/periodicidade-compra.api';
import { IPeriodicidadeCompraSummary } from 'app/domains/environment/periodicidade-compra/interfaces/periodicidade-compra-list-request.interface';
import { ToastService } from '../toast/services/toast.service';

@Component({
  selector: 'sgt-periodicidade-compra-multi-select',
  templateUrl: './periodicidade-compra-multi-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PeriodicidadeCompraMultiSelectComponent),
      multi: true
    }
  ]
})
export class PeriodicidadeCompraMultiSelectComponent
  extends CustomControlValueAccessor<number[]>
  implements OnInit {
  // Controle interno de estado
  private _loading = false;
  private loaded = false;

  private get loading(): boolean {
    return this._loading;
  }

  private set loading(carregando: boolean) {
    this._loading = carregando;
    this.settings = { ...this.settings, isLoading: carregando };
  }

  private defaultSettings: IMultiSelectSettings = {
    placeholder: 'Periodicidades de compras',
    singular: 'Periodicidade de compra',
    plural: 'Periodicidades de compras',
    searchEmptyResultMessage: 'Nenhuma periodicidade de compra encontrada'
  };

  @Input() settings: IMultiSelectSettings;

  @Output() changed = new EventEmitter<number[]>();

  @ViewChild('multiSelectRef')
  multiSelectComponent: MultiSelectComponent;

  selectorOptionsPeriodicidadeCompras: IOption[] = [];

  filter: IPeriodicidadeCompraFilter = {
    situacoes: EnumSituacao.ATIVO
  };

  constructor(
    private toastService: ToastService,
    private periodicidadeCompraApi: PeriodicidadeCompraApi
  ) {
    super();
  }

  ngOnInit(): void {
    this.settings = {
      ...this.defaultSettings,
      ...this.settings
    };
  }

  onFirstOpened(isFirstOpened: boolean): void {
    if (isFirstOpened) {
      this.loadPeriodicidadeCompraIfNeeded();
    }
  }

  // Se as opções ainda não foram carregadas, o carregamento é disparado.
  setSelection(selectedIds: number[]): void {
    const value = selectedIds || [];
    this.value = value;

    this.ensureSelectionApplied(value);
  }

  clearSelection(): void {
    this.value = [];
    this.updateCheckedPeriodicidadeCompraInternal([]);
    this.changed.emit([]);
  }

  // Chamado quando o FormControl escreve valor
  protected updateValue(value: number[] | null): void {
    this.ensureSelectionApplied(value || []);
  }

  protected onDisabledStateChange(isDisabled: boolean): void {
    this.settings = {
      ...this.settings,
      isDisabled
    };
  }

  // Carrega opções somente uma vez.
  // Aplica seleção após o load.
  private loadPeriodicidadeCompraIfNeeded(): void {
    if (this.loaded || this.loading) {
      return;
    }

    this.loading = true;

    this.periodicidadeCompraApi
      .fetchPeriodicidadesComprasList(this.filter)
      .subscribe({
        next: (response) => {
          this.buildSelectOptions(response.data);
          this.syncMultiSelectPeriodicidadeCompra();

          this.loaded = true;
          this.loading = false;

          // Aplica seleção pendente
          if (this.value?.length) {
            this.updateCheckedPeriodicidadeCompraInternal(this.value);
          }
        },
        error: (err: ApiError) => {
          this.loading = false;
          this.toastService.error(err.message, {
            title: err.cause || 'Aconteceu algo inesperado.',
            duration: 5000
          });
        }
      });
  }

  // Atualiza o estado checked com base nos IDs selecionados
  private updateCheckedPeriodicidadeCompraInternal(
    selectedIds: number[]
  ): void {
    this.selectorOptionsPeriodicidadeCompras.forEach((option) => {
      option.isChecked = selectedIds.includes(option.id);
    });

    this.syncMultiSelectPeriodicidadeCompra();
  }

  // Sincroniza manualmente com o componente filho
  private syncMultiSelectPeriodicidadeCompra(): void {
    if (!this.multiSelectComponent) {
      return;
    }

    this.multiSelectComponent.options = [
      ...this.selectorOptionsPeriodicidadeCompras
    ];
    this.multiSelectComponent.filteredOptions = [
      ...this.selectorOptionsPeriodicidadeCompras
    ];
    this.multiSelectComponent.totalSelectedOptions();
    this.multiSelectComponent.changePlaceholder();
  }

  private ensureSelectionApplied(selectedIds: number[]): void {
    if (this.selectorOptionsPeriodicidadeCompras.length === 0) {
      this.loadPeriodicidadeCompraIfNeeded();
    } else {
      this.updateCheckedPeriodicidadeCompraInternal(selectedIds);
    }
  }

  // Disparado quando o usuário altera a seleção manualmente
  onSelectionChange(): void {
    const selected = this.selectorOptionsPeriodicidadeCompras
      .filter((opt) => opt.isChecked)
      .map((opt) => opt.id);

    this.emitChange(selected);
    this.changed.emit(selected);
    this.markAsTouched();
  }

  // Constrói as opções para o seletor
  private buildSelectOptions(
    periodicidadeCompraList: IPeriodicidadeCompraSummary[]
  ): void {
    const currentValue = this.value || [];
    const listaData = periodicidadeCompraList ?? [];

    this.selectorOptionsPeriodicidadeCompras = listaData.map(
      (periodicidade) => ({
        id: periodicidade.id,
        label: periodicidade.nome,
        isChecked: currentValue.includes(periodicidade.id)
      })
    );
  }
}
